// 📂 frontend/src/components/TemplateManager.tsx
import React, { useState, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';
import EmailEditor from './EmailEditor';
import { useToast } from '../hooks/useToast';
import emailApi from '../services/emailApi';
import ConfirmModal from './ConfirmModal';
import { useConfirm } from '../hooks/useConfirm';

interface Template {
  id: number;
  name: string;
  subject: string;
  content: string;
  category: string;
  tags: string[];
  isPublic: boolean;
  createdBy: number;
  createdAt: Date;
  usageCount: number;
  variables: string[];
}

interface TemplateManagerProps {
  templates: Template[];
  onTemplateCreate: (template: Record<string, unknown>) => void;
  onTemplateUpdate: (id: number, template: Record<string, unknown>) => void;
  onTemplateDelete: (id: number) => void;
  onTemplateUse: (template: Template) => void;
  currentUserId: number;
  isAdmin: boolean;
}

const TemplateManager: React.FC<TemplateManagerProps> = ({
  templates,
  onTemplateCreate,
  onTemplateUpdate,
  onTemplateDelete,
  onTemplateUse,
  currentUserId,
  isAdmin
}) => {
  const { theme, currentTheme } = useTheme();
  const { toast } = useToast();
  const { confirmState, confirm, cancelConfirm } = useConfirm();
  
  // State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const stripHtml = (h: string) => h ? h.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().substring(0, 200) : "";
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showPreview, setShowPreview] = useState<Template | null>(null);
  const [importExportModal, setImportExportModal] = useState<'import' | 'export' | null>(null);

  // Form state pentru creare/editare
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    category: 'General',
    tags: '',
    isPublic: false,
    variables: [] as string[]
  });

  // Categorii predefinite
  const categories = [
    'General',
    'Marketing',
    'Newsletter',
    'Notificare',
    'Invitație',
    'Confirmare',
    'Reminder',
    'Anunț',
    'Personal'
  ];

  // Variabile disponibile
  const availableVariables = [
    { key: '{nume}', desc: 'Numele destinatarului' },
    { key: '{email}', desc: 'Email-ul destinatarului' },
    { key: '{data}', desc: 'Data curentă' },
    { key: '{ora}', desc: 'Ora curentă' },
    { key: '{companie}', desc: 'Numele companiei' },
    { key: '{link}', desc: 'Link personalizat' },
    { key: '{cod}', desc: 'Cod unic' }
  ];

  // Resetare formular
  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      content: '',
      category: 'General',
      tags: '',
      isPublic: false,
      variables: []
    });
    setEditingTemplate(null);
  };

  // Populare formular pentru editare
  useEffect(() => {
    if (editingTemplate) {
      setFormData({
        name: editingTemplate.name,
        subject: editingTemplate.subject,
        content: editingTemplate.content,
        category: editingTemplate.category,
        tags: editingTemplate.tags.join(', '),
        isPublic: editingTemplate.isPublic,
        variables: Array.isArray(editingTemplate.variables) ? editingTemplate.variables : []
      });
      setShowCreateModal(true);
    }
  }, [editingTemplate]);

  // Detectare variabile în conținut
  const detectVariables = (text: string): string[] => {
    const regex = /\{([^}]+)\}/g;
    const matches = text.match(regex) || [];
    return Array.from(new Set(matches));
  };

  // Handler pentru schimbări în formular
  const handleFormChange = (field: string, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Auto-detectare variabile când se modifică subject sau content
    if (field === 'subject' || field === 'content') {
      const detectedVars = [
        ...detectVariables(field === 'subject' ? String(value) : formData.subject),
        ...detectVariables(field === 'content' ? String(value) : formData.content)
      ];
      setFormData(prev => ({ ...prev, variables: Array.from(new Set(detectedVars)) }));
    }
  };

  // Salvare template
  const handleSave = async () => {
    if (!formData.name || !formData.subject || !formData.content) {
      toast.error('Eroare', 'Toate câmpurile sunt obligatorii!');
      return;
    }

    const templateData = {
      ...formData,
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
    };

    try {
      if (editingTemplate) {
        await onTemplateUpdate(editingTemplate.id, templateData);
        toast.success('Succes', 'Template actualizat cu succes!');
      } else {
        await onTemplateCreate(templateData);
        toast.success('Succes', 'Template creat cu succes!');
      }
      
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      toast.error('Eroare', 'Nu am putut salva template-ul');
    }
  };

  // Inserare variabilă în conținut
  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('template-content') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.content;
      const newText = text.substring(0, start) + variable + text.substring(end);
      handleFormChange('content', newText);
      
      // Repoziționare cursor
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  // Export template-uri
  const handleExport = () => {
    const exportData = templates.map(t => ({
      name: t.name,
      subject: t.subject,
      content: t.content,
      category: t.category,
      tags: t.tags
    }));
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `email-templates-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    toast.success('Export', `${templates.length} template-uri exportate!`);
    setImportExportModal(null);
  };

  // Import template-uri
  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const content = e.target?.result as string;
        const importedTemplates = JSON.parse(content);
        
        if (!Array.isArray(importedTemplates)) {
          throw new Error('Format invalid');
        }
        
        // Importă fiecare template
        for (const template of importedTemplates) {
          await onTemplateCreate(template);
        }
        
        toast.success('Import', `${importedTemplates.length} template-uri importate!`);
        setImportExportModal(null);
      } catch (error) {
        toast.error('Eroare', 'Fișier invalid sau format incorect');
      }
    };
    reader.readAsText(file);
  };

  // Filtrare template-uri
  const getFilteredTemplates = () => {
    let filtered = templates;
    
    // Filtrare după proprietar
    if (filter === 'my') {
      filtered = filtered.filter(t => t.createdBy === currentUserId);
    } else if (filter === 'public') {
      filtered = filtered.filter(t => t.isPublic);
    }
    
    // Filtrare după categorie
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }
    
    // Căutare
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    return filtered.sort((a, b) => b.usageCount - a.usageCount);
  };

  // Preview cu înlocuire variabile
  const getPreviewContent = (template: Template) => {
    let content = template.content;
    const sampleData: Record<string, string> = {
      '{nume}': 'Ion Popescu',
      '{email}': 'ion.popescu@example.com',
      '{data}': new Date().toLocaleDateString('ro-RO'),
      '{ora}': new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }),
      '{companie}': 'Compania Demo SRL',
      '{link}': 'https://example.com/link-demo',
      '{cod}': 'DEMO-' + Math.random().toString(36).substr(2, 9).toUpperCase()
    };
    
    Object.entries(sampleData).forEach(([key, value]) => {
      content = content.replace(new RegExp(key, 'g'), value);
    });
    
    return content;
  };

  // Helper pentru a asigura că variables este array
  const ensureVariablesArray = (variables: string | string[] | undefined): string[] => {
    if (Array.isArray(variables)) return variables;
    if (typeof variables === 'string' && variables) {
      try {
        const parsed = JSON.parse(variables);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  return (
    <div>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <h2 style={{ margin: '0 0 8px 0' }}>✉️ Template-uri Email</h2>
          <p style={{ margin: 0, color: theme.colors.textSecondary, fontSize: '14px' }}>
            {getFilteredTemplates().length} template-uri disponibile
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setImportExportModal('import')}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              color: theme.colors.text,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.medium,
              cursor: 'pointer'
            }}
          >
            📥 Import
          </button>
          <button
            onClick={() => setImportExportModal('export')}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              color: theme.colors.text,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.medium,
              cursor: 'pointer'
            }}
          >
            📤 Export
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: theme.colors.primary,
              color: '#ffffff',
              border: 'none',
              borderRadius: theme.borderRadius.medium,
              cursor: 'pointer'
            }}
          >
            ➕ Template Nou
          </button>
        </div>
      </div>

      {/* Filtre */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
          <button
            onClick={() => setFilter('all')}
            style={{
              padding: '6px 12px',
              backgroundColor: filter === 'all' ? theme.colors.primary : 'transparent',
              color: filter === 'all' ? '#ffffff' : theme.colors.text,
              border: `1px solid ${filter === 'all' ? theme.colors.primary : theme.colors.border}`,
              borderRadius: theme.borderRadius.small,
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Toate ({templates.length})
          </button>
          <button
            onClick={() => setFilter('my')}
            style={{
              padding: '6px 12px',
              backgroundColor: filter === 'my' ? theme.colors.primary : 'transparent',
              color: filter === 'my' ? '#ffffff' : theme.colors.text,
              border: `1px solid ${filter === 'my' ? theme.colors.primary : theme.colors.border}`,
              borderRadius: theme.borderRadius.small,
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Template-urile mele ({templates.filter(t => t.createdBy === currentUserId).length})
          </button>
          <button
            onClick={() => setFilter('public')}
            style={{
              padding: '6px 12px',
              backgroundColor: filter === 'public' ? theme.colors.primary : 'transparent',
              color: filter === 'public' ? '#ffffff' : theme.colors.text,
              border: `1px solid ${filter === 'public' ? theme.colors.primary : theme.colors.border}`,
              borderRadius: theme.borderRadius.small,
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Publice ({templates.filter(t => t.isPublic).length})
          </button>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            placeholder="🔍 Caută template-uri..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: theme.borderRadius.medium,
              border: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.background,
              color: theme.colors.text
            }}
          />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: theme.borderRadius.medium,
              border: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.background,
              color: theme.colors.text
            }}
          >
            <option value="all">Toate categoriile</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista template-uri */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '16px'
      }}>
        {getFilteredTemplates().map(template => {
          const templateVariables = ensureVariablesArray(template.variables);
          
          return (
            <div
              key={template.id}
              style={{
                backgroundColor: theme.colors.surface,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.large,
                padding: '20px',
                cursor: 'pointer',
                transition: theme.transitions.normal,
                position: 'relative'
              }}
              className="template-card"
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = theme.shadows.large;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Header */}
              <div style={{ marginBottom: '12px' }}>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>{template.name}</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '2px 8px',
                    backgroundColor: theme.colors.primary + '22',
                    color: theme.colors.primary,
                    borderRadius: '12px',
                    fontSize: '11px'
                  }}>
                    {template.category}
                  </span>
                  {template.isPublic && (
                    <span style={{
                      padding: '2px 8px',
                      backgroundColor: theme.colors.success + '22',
                      color: theme.colors.success,
                      borderRadius: '12px',
                      fontSize: '11px'
                    }}>
                      Public
                    </span>
                  )}
                  {templateVariables.length > 0 && (
                    <span style={{
                      padding: '2px 8px',
                      backgroundColor: theme.colors.accent + '22',
                      color: theme.colors.accent,
                      borderRadius: '12px',
                      fontSize: '11px'
                    }}>
                      {templateVariables.length} variabile
                    </span>
                  )}
                </div>
              </div>

              {/* Subject preview */}
              <p style={{ 
                margin: '0 0 8px 0',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                📧 {template.subject}
              </p>

              {/* Content preview */}
              <p style={{
                margin: '0 0 12px 0',
                fontSize: '13px',
                color: theme.colors.textSecondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>
                {stripHtml(template.content)}
              </p>

              {/* Tags */}
              {template.tags.length > 0 && (
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  {template.tags.slice(0, 3).map(tag => (
                    <span
                      key={tag}
                      style={{
                        padding: '2px 6px',
                        backgroundColor: theme.colors.background,
                        borderRadius: '8px',
                        fontSize: '11px',
                        color: theme.colors.textSecondary
                      }}
                    >
                      #{tag}
                    </span>
                  ))}
                  {template.tags.length > 3 && (
                    <span style={{ fontSize: '11px', color: theme.colors.textSecondary }}>
                      +{template.tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Footer */}
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: `1px solid ${theme.colors.border}`,
                paddingTop: '12px'
              }}>
                <span style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                  Folosit de {template.usageCount} ori
                </span>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPreview(template);
                    }}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: theme.colors.primary + '22',
                      color: theme.colors.primary,
                      border: 'none',
                      borderRadius: theme.borderRadius.small,
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                    title="Preview"
                  >
                    👁️
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onTemplateUse(template);
                    }}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: theme.colors.success + '22',
                      color: theme.colors.success,
                      border: 'none',
                      borderRadius: theme.borderRadius.small,
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                    title="Folosește"
                  >
                    ✉️
                  </button>
                  {(template.createdBy === currentUserId || isAdmin) && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingTemplate(template);
                        }}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: theme.colors.accent + '22',
                          color: theme.colors.accent,
                          border: 'none',
                          borderRadius: theme.borderRadius.small,
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                        title="Editează"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const confirmed = await confirm({
                            title: 'Confirmare ștergere',
                            message: `Ștergi template-ul "${template.name}"?`,
                            variant: 'danger',
                            confirmText: 'Șterge',
                            cancelText: 'Anulează'
                          });
                          if (confirmed) {
                            onTemplateDelete(template.id);
                          }
                        }}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: theme.colors.danger + '22',
                          color: theme.colors.danger,
                          border: 'none',
                          borderRadius: theme.borderRadius.small,
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                        title="Șterge"
                      >
                        🗑️
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {getFilteredTemplates().length === 0 && (
        <div style={{ 
          textAlign: 'center',
          padding: '60px 20px',
          color: theme.colors.textSecondary
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
          <h3 style={{ marginBottom: '8px' }}>Nu există template-uri</h3>
          <p style={{ marginBottom: '20px' }}>
            {searchTerm ? 'Nu am găsit template-uri care să corespundă căutării.' : 'Creează primul tău template!'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                padding: '12px 24px',
                backgroundColor: theme.colors.primary,
                color: '#ffffff',
                border: 'none',
                borderRadius: theme.borderRadius.medium,
                cursor: 'pointer'
              }}
            >
              ➕ Creează Template
            </button>
          )}
        </div>
      )}

      {/* Modal Creare/Editare */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => {
          setShowCreateModal(false);
          resetForm();
        }}>
          <div style={{
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.large,
            padding: '30px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: '20px' }}>
              {editingTemplate ? '✏️ Editează Template' : '➕ Template Nou'}
            </h2>

            <div style={{ display: 'grid', gap: '16px' }}>
              {/* Nume */}
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Nume Template *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder="ex: Bun venit client nou"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: theme.borderRadius.medium,
                    border: `1px solid ${theme.colors.border}`,
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text
                  }}
                />
              </div>

              {/* Categorie și Public */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                    Categorie
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: theme.borderRadius.medium,
                      border: `1px solid ${theme.colors.border}`,
                      backgroundColor: theme.colors.background,
                      color: theme.colors.text
                    }}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.isPublic}
                      onChange={(e) => handleFormChange('isPublic', e.target.checked)}
                    />
                    <span>Public</span>
                  </label>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Tag-uri (separate prin virgulă)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => handleFormChange('tags', e.target.value)}
                  placeholder="ex: marketing, newsletter, promo"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: theme.borderRadius.medium,
                    border: `1px solid ${theme.colors.border}`,
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text
                  }}
                />
              </div>

              {/* Subject */}
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Subiect Email *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => handleFormChange('subject', e.target.value)}
                  placeholder="ex: Bun venit, {nume}!"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: theme.borderRadius.medium,
                    border: `1px solid ${theme.colors.border}`,
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text
                  }}
                />
              </div>

              {/* Content cu variabile */}
              <div>
                <label style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '4px',
                  fontWeight: '500'
                }}>
                  <span>Conținut Email *</span>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {availableVariables.map(v => (
                      <button
                        key={v.key}
                        type="button"
                        onClick={() => insertVariable(v.key)}
                        style={{
                          padding: '2px 8px',
                          backgroundColor: theme.colors.primary + '22',
                          color: theme.colors.primary,
                          border: 'none',
                          borderRadius: theme.borderRadius.small,
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                        title={v.desc}
                      >
                        {v.key}
                      </button>
                    ))}
                  </div>
                </label>
                <EmailEditor
                  initialContent={formData.content}
                  onChange={(content: string) => handleFormChange(content, content)}
                  placeholder="Scrie continutul email-ului aici..."
                  minHeight={350}
                />
                {formData.variables.length > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: theme.colors.textSecondary }}>
                    Variabile detectate: {formData.variables.join(', ')}
                  </div>
                )}
              </div>
            </div>

            {/* Butoane */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  color: theme.colors.text,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.medium,
                  cursor: 'pointer'
                }}
              >
                Anulează
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: '10px 20px',
                  backgroundColor: theme.colors.primary,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: theme.borderRadius.medium,
                  cursor: 'pointer'
                }}
              >
                {editingTemplate ? 'Actualizează' : 'Creează'} Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Preview */}
      {showPreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowPreview(null)}>
          <div style={{
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.large,
            padding: '30px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: '20px' }}>👁️ Preview Template</h2>
            
            <div style={{
              backgroundColor: theme.colors.background,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.medium,
              padding: '20px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>
                <strong>Subiect:</strong> {showPreview.subject}
              </p>
              <iframe srcDoc={getPreviewContent(showPreview)} style={{ border: 'none', width: '100%', minHeight: '350px', borderRadius: '8px', background: '#fff' }} />
            </div>

            {ensureVariablesArray(showPreview.variables).length > 0 && (
              <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginBottom: '20px' }}>
                <p style={{ margin: '0 0 4px 0' }}><strong>Variabile folosite:</strong></p>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {ensureVariablesArray(showPreview.variables).map((v, index) => (
                    <li key={`var-${index}`}>{v}</li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPreview(null)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  color: theme.colors.text,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.medium,
                  cursor: 'pointer'
                }}
              >
                Închide
              </button>
              <button
                onClick={() => {
                  onTemplateUse(showPreview);
                  setShowPreview(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: theme.colors.primary,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: theme.borderRadius.medium,
                  cursor: 'pointer'
                }}
              >
                ✉️ Folosește Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Import/Export */}
      {importExportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setImportExportModal(null)}>
          <div style={{
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.large,
            padding: '30px',
            maxWidth: '400px',
            width: '100%'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: '20px' }}>
              {importExportModal === 'import' ? '📥 Import Template-uri' : '📤 Export Template-uri'}
            </h2>

            {importExportModal === 'import' ? (
              <div>
                <p style={{ marginBottom: '20px', color: theme.colors.textSecondary }}>
                  Selectează un fișier JSON cu template-uri pentru import.
                </p>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  style={{ marginBottom: '20px' }}
                />
              </div>
            ) : (
              <div>
                <p style={{ marginBottom: '20px', color: theme.colors.textSecondary }}>
                  Vei exporta {templates.length} template-uri într-un fișier JSON.
                </p>
                <button
                  onClick={handleExport}
                  style={{
                    width: '100%',
                    padding: '10px 20px',
                    backgroundColor: theme.colors.primary,
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: theme.borderRadius.medium,
                    cursor: 'pointer'
                  }}
                >
                  📤 Descarcă Template-uri
                </button>
              </div>
            )}

            <button
              onClick={() => setImportExportModal(null)}
              style={{
                width: '100%',
                padding: '10px 20px',
                marginTop: '10px',
                backgroundColor: 'transparent',
                color: theme.colors.text,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.medium,
                cursor: 'pointer'
              }}
            >
              Anulează
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        onConfirm={confirmState.onConfirm}
        onCancel={cancelConfirm}
      />
    </div>
  );
};

export default TemplateManager;